import React from "react";
import LegalLayout, { LegalSection } from "@/pages/LegalLayout";

export default function LegalPrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      lastUpdated="February 1, 2026"
      testid="legal-privacy-page"
    >
      <p>
        This Privacy Policy explains how <strong>Sonrie LLC</strong>{" "}
        (&ldquo;onebrain,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo;) collects,
        uses, and shares personal information when you use the onebrain shared
        AI chat workspace product (the &ldquo;Service&rdquo;).
      </p>

      <LegalSection
        heading="1. Information We Collect"
        testid="legal-privacy-section-collect"
      >
        <p>We collect the following categories of information:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>
            <strong>Account data:</strong> your email address, display name,
            and hashed password.
          </li>
          <li>
            <strong>Workspace data:</strong> workspace names, membership lists,
            roles, invitations.
          </li>
          <li>
            <strong>Chat content:</strong> messages, attachments, and
            assistant responses inside your workspaces.
          </li>
          <li>
            <strong>Usage data:</strong> aggregate counts of messages,
            input/output tokens, billed cost, and which user generated each
            assistant call. Used for billing and abuse detection only.
          </li>
          <li>
            <strong>Technical data:</strong> IP address, user agent, and
            session cookies needed to keep you signed in (HttpOnly, 7-day
            expiry).
          </li>
        </ul>
      </LegalSection>

      <LegalSection
        heading="2. How We Use Your Information"
        testid="legal-privacy-section-use"
      >
        <ul className="list-disc pl-6 space-y-1.5">
          <li>
            To operate the Service: authenticate you, route messages to your
            workspace members, and call the AI assistant on your behalf.
          </li>
          <li>
            To bill paid plans and track usage allowances.
          </li>
          <li>
            To protect the Service: detect abuse, suspend workspaces that
            violate the Acceptable Use Policy, and investigate incidents.
          </li>
          <li>
            To communicate with you about your account, billing, or material
            changes to the Service.
          </li>
        </ul>
        <p>
          We do <strong>not</strong> use your chat content to train AI models
          (ours or anyone else&rsquo;s). onebrain staff do not read your
          messages except where strictly necessary to investigate a specific
          incident you or another workspace member has reported to us.
        </p>
      </LegalSection>

      <LegalSection
        heading="3. Subprocessors"
        testid="legal-privacy-section-subprocessors"
      >
        <p>
          We share data with the following subprocessors so we can deliver the
          Service:
        </p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>
            <strong>Anthropic, PBC</strong> &mdash; processes the chat content
            you send to the AI assistant in order to generate the
            assistant&rsquo;s reply. Subject to Anthropic&rsquo;s{" "}
            <a
              href="https://www.anthropic.com/legal/privacy"
              target="_blank"
              rel="noreferrer"
              className="underline text-[#1E7BFF] hover:text-[#155EC9]"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href="https://www.anthropic.com/legal/commercial-terms"
              target="_blank"
              rel="noreferrer"
              className="underline text-[#1E7BFF] hover:text-[#155EC9]"
            >
              Commercial Terms
            </a>
            . Per Anthropic&rsquo;s commercial terms, API inputs and outputs
            are not used to train Anthropic&rsquo;s models.
          </li>
          <li>
            <strong>Stripe, Inc.</strong> &mdash; processes payments for paid
            plans. onebrain does not store full payment card numbers.
          </li>
          <li>
            <strong>Cloud hosting provider</strong> &mdash; runs our servers
            and stores our database under a Data Processing Agreement with us.
          </li>
        </ul>
        <p>
          When you send a message that triggers an AI response, the contents
          of that message (and recent context from the same workspace) are
          transmitted to Anthropic for the sole purpose of generating the
          reply. Messages marked <code>/private</code> or sent in a
          human-only thread are not sent to Anthropic.
        </p>
      </LegalSection>

      <LegalSection
        heading="4. Data Retention"
        testid="legal-privacy-section-retention"
      >
        <p>
          We retain chat content for as long as your workspace exists. When a
          workspace owner deletes a workspace, its chat content is removed
          from our primary database within 30 days. Audit logs of
          administrator actions are retained for up to 24 months for security
          and compliance purposes.
        </p>
      </LegalSection>

      <LegalSection
        heading="5. Cookies"
        testid="legal-privacy-section-cookies"
      >
        <p>
          We use a single HttpOnly session cookie to keep you signed in.
          We do not use cookies for advertising or third-party tracking. You
          can clear the cookie by logging out or clearing your browser data.
        </p>
      </LegalSection>

      <LegalSection
        heading="6. Your Rights"
        testid="legal-privacy-section-rights"
      >
        <p>
          Depending on where you live, you may have rights to access,
          correct, export, or delete personal data we hold about you, and to
          object to certain uses. To exercise these rights, contact{" "}
          <strong>info@onebrain.team</strong>. We will respond within 30 days
          (or sooner if required by law).
        </p>
      </LegalSection>

      <LegalSection
        heading="7. Security"
        testid="legal-privacy-section-security"
      >
        <p>
          We use industry-standard safeguards including TLS in transit,
          bcrypt password hashing, encrypted database snapshots, and
          least-privilege access controls. No system is perfectly secure; if
          we become aware of a personal-data breach affecting you, we will
          notify you within the timelines required by applicable law.
        </p>
      </LegalSection>

      <LegalSection
        heading="8. International Transfers"
        testid="legal-privacy-section-international"
      >
        <p>
          onebrain is operated from <strong>Duval County, Florida</strong>. If you
          access the Service from outside this jurisdiction, your data will
          be transferred to and processed in <strong>Duval County, Florida</strong>{" "}
          and the United States (where Anthropic operates). We rely on
          standard contractual clauses or equivalent safeguards where
          applicable.
        </p>
      </LegalSection>

      <LegalSection
        heading="9. Children"
        testid="legal-privacy-section-children"
      >
        <p>
          The Service is not directed to children under 13 (or under 16 in
          the EEA) and we do not knowingly collect their personal information.
        </p>
      </LegalSection>

      <LegalSection
        heading="10. Changes to This Policy"
        testid="legal-privacy-section-changes"
      >
        <p>
          We may update this Privacy Policy from time to time. Material
          changes will be communicated by email or in-app notice prior to
          taking effect.
        </p>
      </LegalSection>

      <LegalSection
        heading="11. Contact"
        testid="legal-privacy-section-contact"
      >
        <p>
          Questions about this Privacy Policy or our handling of your data?
          Contact us at <strong>info@onebrain.team</strong>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
